import * as ko from 'knockout';

import { getPaymentHistory, getPaymentYears, PaymentHistoryDto, YearDto } from '@pressreader/commerce-api';
import { getBasePath } from '@pressreader/routing';
import { productDescriptions, productTypes } from '@pressreader/src/account/viewmodels/constants';
import { logAndAlertError } from '@pressreader/src/shared/errors/error.handlers';
import { parseDateString } from '@pressreader/utils';

import resources from 'nd.res';

interface Item {
    id: string;
    productName: string;
    productDescription: string;
    formattedDate: string;
    formattedAmount: string;
    hideBillingInfo: boolean;
}

function createMutableValue<T>(value: T = null) {
    return { current: ko.observable<T>(value), old: ko.observable<T>(value) };
}

type KeyValuePair<K, V> = { Key: K; Value: V };

function createSelectList<K, V>(items: KeyValuePair<K, V>[], selectedValue: V) {
    return {
        list: items,
        selected: createMutableValue(selectedValue),
    };
}

const emptyListYears = createSelectList<YearDto, YearDto>([], 0);

class PaymentHistoryViewModel {
    isLoading: KnockoutObservable<boolean>;
    years: KnockoutObservable<typeof emptyListYears>;
    year: KnockoutObservable<YearDto>;
    items: KnockoutObservableArray<Item>;

    private _subscriptions: KnockoutSubscription[];

    constructor() {
        this.isLoading = ko.observable(false);
        this.years = ko.observable(emptyListYears);
        this.year = ko.observable<YearDto>();
        this.items = ko.observableArray<Item>([]);

        this._subscriptions = [];
        this._subscriptions.push(
            this.year.subscribe(async year => {
                this.isLoading(true);
                try {
                    await getPaymentHistory(year)
                        .then(this.mapHistoryItems)
                        .then((items: Item[]) => this.items(items));
                } catch (error) {
                    logAndAlertError(error);
                } finally {
                    this.isLoading(false);
                }
            }),
        );
    }

    mapHistoryItems(items: PaymentHistoryDto[]): Item[] {
        return items.map(item => {
            const result: Item = {
                id: item.id,
                productName: null,
                productDescription: null,
                formattedDate: item.formattedDate,
                formattedAmount: item.formattedAmount,
                hideBillingInfo: item.hideBillingInfo,
            };

            switch (item.productType) {
                case productTypes.subscription: {
                    result.productName = item.productName;
                    result.productDescription = productDescriptions.subscription;
                    break;
                }
                case productTypes.singleIssue: {
                    // Get the first item (backend always must send this item)
                    if (item && Array.isArray(item.orderItems) && item.orderItems[0]) {
                        const orderItem = item.orderItems[0];
                        result.productName = orderItem.publicationName;

                        const issueDateFormatted = resources.formatDate(parseDateString(orderItem.issueDate), 'v7.Client.DateFormat.Issue');
                        // Payment Date = Issue (Magazine) Date, then is single issue, else "Archive"
                        result.productDescription = orderItem.isArchive ? productDescriptions.archiveIssue : productDescriptions.singleIssue;

                        result.productDescription += ` (${issueDateFormatted})`;
                    } else {
                        result.productName = productDescriptions.singleIssue;
                        result.productDescription = item.productName;
                    }

                    break;
                }
                case productTypes.bundle: {
                    result.productName = item.productName.trim();
                    result.productDescription = item.orderItems
                        .map(orderItem => {
                            const issueDateFormatted = resources.formatDate(parseDateString(orderItem.issueDate), 'v7.Client.DateFormat.Issue');
                            let name = orderItem.publicationName;
                            if (issueDateFormatted === '') {
                                name += ` (${productDescriptions.subscription})`;
                            } else {
                                name += orderItem.isArchive ? productDescriptions.archiveIssue : productDescriptions.singleIssue;
                                name += `, (${issueDateFormatted})`;
                            }
                            return name;
                        })
                        .join(', ');
                    break;
                }
                default: {
                    throw new Error('Invalid productType');
                }
            }

            return result;
        });
    }

    openReceipt(item) {
        if (!item.hideBillingInfo) {
            window.location.assign(`${getBasePath()}Accounting/Receipt/${item.id}`);
        }
        return false;
    }

    async load() {
        this.isLoading(true);
        try {
            const years = await getPaymentYears();

            const selectList = createSelectList(
                years.map(m => ({
                    Key: +m,
                    Value: +m,
                })),
                years.length > 0 ? years[0] : 0,
            );

            this.years(selectList);

            this._subscriptions.push(this.years().selected.current.subscribe(s => this.year(s)));

            if (years.length > 0) {
                this.year(years[0]);
            }
        } catch (err) {
            logAndAlertError(err);
        } finally {
            this.isLoading(false);
        }
    }

    dispose() {
        this._subscriptions.forEach(s => s.dispose());
        this._subscriptions.length = 0;
    }
}

export default PaymentHistoryViewModel;
